import React, { useState } from 'react';

const f0 = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const f2 = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const FUND_PRESETS = [
  { name: 'Vacation',        em: '✈️',  monthly: 100, target: 1200 },
  { name: 'Car Maintenance', em: '🔧',  monthly: 50,  target: 600  },
  { name: 'Holiday Gifts',   em: '🎁',  monthly: 80,  target: 960  },
  { name: 'Emergency Fund',  em: '🆘',  monthly: 200, target: 5000 },
  { name: 'Home Repairs',    em: '🏠',  monthly: 75,  target: 2000 },
  { name: 'New Phone',       em: '📱',  monthly: 40,  target: 800  },
];

export default function SinkingFunds({ state, updateState, showToast }) {
  const funds = state.sinkingFunds || [];
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', em: '🎯', target: '', monthly: '', balance: '' });
  const [depositId, setDepositId] = useState(null);
  const [depositAmt, setDepositAmt] = useState('');

  const totalMonthly = funds.reduce((s, f) => s + (+f.monthly || 0), 0);

  const addFund = () => {
    if (!form.name || !form.target) return;
    const fund = {
      id: 'sf_' + Date.now(),
      name: form.name.trim(),
      em: form.em || '🎯',
      target: parseFloat(form.target) || 0,
      monthly: parseFloat(form.monthly) || 0,
      balance: parseFloat(form.balance) || 0,
    };
    updateState({ sinkingFunds: [...funds, fund] });
    setForm({ name: '', em: '🎯', target: '', monthly: '', balance: '' });
    setAdding(false);
    showToast('Sinking fund created!');
  };

  const applyPreset = (p) => setForm({ name: p.name, em: p.em, target: String(p.target), monthly: String(p.monthly), balance: '' });

  const deposit = (id) => {
    const amt = parseFloat(depositAmt);
    if (!amt || amt <= 0) return;
    updateState({ sinkingFunds: funds.map(f => f.id === id ? { ...f, balance: (f.balance || 0) + amt } : f) });
    setDepositId(null);
    setDepositAmt('');
    showToast('Deposit added!');
  };

  const removeFund = (id) => {
    if (!window.confirm('Remove this sinking fund?')) return;
    updateState({ sinkingFunds: funds.filter(f => f.id !== id) });
  };

  return (
    <div>
      {/* Summary */}
      <div className="card cp" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 700, letterSpacing: 1 }}>SINKING FUNDS</div>
            <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>Planned savings for irregular expenses</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--ylw)' }}>{f2(totalMonthly)}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--mut)' }}>/mo</span></div>
            <div style={{ fontSize: 10, color: 'var(--mut)' }}>total monthly set-aside</div>
          </div>
        </div>
        <div style={{ height: 4, background: 'var(--surf2)', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: Math.min(100, (funds.reduce((s, f) => s + (f.balance || 0), 0) / Math.max(1, funds.reduce((s, f) => s + f.target, 0))) * 100) + '%', background: 'var(--ylw)', borderRadius: 999 }} />
        </div>
      </div>

      {/* Funds list */}
      {funds.map(f => {
        const pct = f.target > 0 ? Math.min(100, Math.round((f.balance / f.target) * 100)) : 0;
        const monthsLeft = f.monthly > 0 && f.target > (f.balance || 0) ? Math.ceil((f.target - f.balance) / f.monthly) : 0;
        return (
          <div key={f.id} className="card cp" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 28 }}>{f.em}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>{f.name}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                  {f2(f.monthly)}/mo · {monthsLeft > 0 ? `~${monthsLeft} months left` : pct >= 100 ? '✓ Funded!' : 'No monthly set'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--grn)' }}>{f0(f.balance || 0)}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>of {f0(f.target)}</div>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ height: 8, background: 'var(--surf2)', borderRadius: 999, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? 'var(--grn)' : 'var(--ylw)', borderRadius: 999, transition: 'width .4s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginBottom: 10 }}>
              <span>{pct}% saved</span>
              <span>{f0(Math.max(0, f.target - (f.balance || 0)))} remaining</span>
            </div>

            {/* Actions */}
            {depositId === f.id ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="ob-inp" type="number" placeholder="Amount $" value={depositAmt} onChange={e => setDepositAmt(e.target.value)} style={{ flex: 1, margin: 0 }} />
                <button onClick={() => deposit(f.id)} style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>Add</button>
                <button onClick={() => setDepositId(null)} style={{ padding: '7px 10px', borderRadius: 8, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => { setDepositId(f.id); setDepositAmt(''); }} style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'rgba(255,217,61,.1)', border: '1px solid rgba(255,217,61,.3)', color: 'var(--ylw)', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
                  + Deposit
                </button>
                <button onClick={() => removeFund(f.id)} style={{ padding: '7px 10px', borderRadius: 8, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 12, cursor: 'pointer' }}>✕</button>
              </div>
            )}
          </div>
        );
      })}

      {/* Add fund */}
      <button onClick={() => setAdding(s => !s)} style={{ width: '100%', padding: '11px', borderRadius: 12, background: adding ? 'none' : 'var(--ylw)', color: adding ? 'var(--mut)' : '#000', fontWeight: 800, fontSize: 13, border: adding ? '1px solid var(--bdr)' : 'none', cursor: 'pointer', marginBottom: 12 }}>
        {adding ? 'Cancel' : '+ New Sinking Fund'}
      </button>

      {adding && (
        <div className="card cp">
          <div className="st" style={{ marginBottom: 10 }}>Quick Presets</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {FUND_PRESETS.map(p => (
              <button key={p.name} onClick={() => applyPreset(p)} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, border: '1.5px solid var(--bdr)', background: 'none', color: 'var(--txt)', cursor: 'pointer' }}>
                {p.em} {p.name}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input className="ob-inp" placeholder="Emoji" value={form.em} onChange={e => setForm(f => ({ ...f, em: e.target.value }))} style={{ width: 60, margin: 0, textAlign: 'center' }} />
            <input className="ob-inp" placeholder="Fund name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 1, margin: 0 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <input className="ob-inp" type="number" placeholder="Target amount $" value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))} style={{ flex: 1, margin: 0 }} />
            <input className="ob-inp" type="number" placeholder="Monthly $ contribution" value={form.monthly} onChange={e => setForm(f => ({ ...f, monthly: e.target.value }))} style={{ flex: 1, margin: 0 }} />
          </div>
          <input className="ob-inp" type="number" placeholder="Current balance (if any) $" value={form.balance} onChange={e => setForm(f => ({ ...f, balance: e.target.value }))} style={{ marginBottom: 10 }} />
          <button onClick={addFund} style={{ width: '100%', padding: '10px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>
            Create Fund
          </button>
        </div>
      )}
    </div>
  );
}
